import { useState, useMemo } from 'react';
import { Option } from '../../utils/option';
import { NextRouter } from 'next/router';
import withPageInit from '../../utils/hooks/withPageInit';
import useNameInput from '../../utils/hooks/useNameInput';
import UpdatePresenter from './updatePresenter';
import useLocationSelect from '../../utils/hooks/useLocationSelect';
import useNicknameInput from '../../utils/hooks/useNicknameInput';

function useUpdateUser(router: Option<NextRouter>) {
  const [
    firstName,
    onChangeFirstName,
    firstNameErr,
    setFirstName,
    firstNameHasError,
    setFirstNameNotTouched,
    clearFirstNameError
  ] = useNameInput('', 'firstName');

  const [
    lastName,
    onChangeLastName,
    lastNameErr,
    setLastName,
    lastNameHasError,
    setLastNameNotTouched,
    clearLastNameError
  ] = useNameInput('', 'lastName');

  const [
    location,
    onChangeLocation,
    locationErr,
    setLocation,
    locationHasError,
    setLocationNotTouched,
    clearLocationError
  ] = useLocationSelect('');

  const [
    nickname,
    onChangeNickname,
    nicknameErr,
    setNickname,
    nicknameHasError,
    setNicknameNotTouched,
    clearNicknameError
  ] = useNicknameInput('');

  const updateNameEnabled = useMemo(() => {
    return (
      firstNameHasError ||
      lastNameHasError ||
      firstName.length === 0 ||
      lastName.length === 0
    );
  }, [firstNameHasError, lastNameHasError, firstName, lastName]);

  const updateLocationEnabled = useMemo(() => {
    return locationHasError || location.length === 0;
  }, [locationHasError, location]);

  const updateNicknameEnabled = useMemo(() => {
    return nicknameHasError || nickname.length === 0;
  }, [nicknameHasError, nickname]);

  const clearUserNameForm = () => {
    setFirstName('');
    setLastName('');
    clearFirstNameError();
    clearLastNameError();
    setServerErrMsg('');
    setFirstNameNotTouched(true);
    setLastNameNotTouched(true);
  };

  const clearUserLocationForm = () => {
    setLocation('');
    clearLocationError();
    setServerErrMsg('');
    setLocationServerErrMsg('');
    setLocationNotTouched(true);
  };

  const clearUserNicknameForm = () => {
    setNickname('');
    clearNicknameError();
    setServerErrMsg('');
    setNicknameNotTouched(true);
  };

  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [serverErrMsg, setServerErrMsg] = useState('');
  const [locationServerErrMsg, setLocationServerErrMsg] = useState('');

  const userUpdatePresenter = useMemo(() => {
    return new UpdatePresenter(router, setIsUpdateLoading, setServerErrMsg);
  }, []);

  return {
    isUpdateLoading,
    firstName,
    lastName,
    location,
    nickname,
    onChangeFirstName,
    onChangeLastName,
    onChangeLocation,
    onChangeNickname,
    firstNameErr,
    lastNameErr,
    locationErr,
    nicknameErr,
    serverErrMsg,
    locationServerErrMsg,
    updateNameEnabled,
    updateLocationEnabled,
    updateNicknameEnabled,
    clearUserNameForm,
    clearUserLocationForm,
    clearUserNicknameForm,
    userUpdatePresenter
  };
}

export default withPageInit(useUpdateUser);
